import { HStack, useDisclosure, Drawer, VStack, Button, Icon, Portal, CloseButton } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom'
import { FiHome, FiGrid, FiMoreHorizontal } from 'react-icons/fi'
import {
  CORE_NAV,
  isPrefixActive,
  resolveVisibleModule,
  type ModuleNavEntry,
} from '../../../modules/registry'
import { useProductTier } from '../../../hooks/useClientConfig'
import { BottomNavItem } from '../../shared/BottomNavItem'

interface ContextualTabBarProps {
  pathname: string
  onOpenModules: () => void
}

const MAX_NAV_ITEMS = 4

export function ContextualTabBar({ pathname, onOpenModules }: ContextualTabBarProps) {
  const navigate = useNavigate()
  const moreSheet = useDisclosure()
  const tier = useProductTier()
  const activeModule = resolveVisibleModule(pathname, tier)
  const isTopLevel = !activeModule || activeModule.navItems.length === 0

  // Overflow: Hub + up to MAX items; if more, Hub + (MAX-1) + a "More" tab.
  let visibleItems: ModuleNavEntry[] = []
  let overflowItems: ModuleNavEntry[] = []
  if (!isTopLevel) {
    const items = activeModule!.navItems
    if (items.length > MAX_NAV_ITEMS) {
      visibleItems = items.slice(0, MAX_NAV_ITEMS - 1)
      overflowItems = items.slice(MAX_NAV_ITEMS - 1)
    } else {
      visibleItems = items
    }
  }

  return (
    <>
      <HStack
        gap={0}
        bg="surface"
        borderTop="1px"
        borderColor="gray.200"
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex={10}
        minH="64px"
        px={2}
        pb="env(safe-area-inset-bottom, 0px)"
      >
        {isTopLevel ? (
          <>
            <BottomNavItem icon={FiHome} label="Home" to="/dashboard" isActive={pathname === '/dashboard'} />
            <BottomNavItem
              icon={CORE_NAV[0].icon}
              label="Contacts"
              to={CORE_NAV[0].to}
              isActive={isPrefixActive(pathname, CORE_NAV[0].activePrefixes)}
            />
            <BottomNavItem
              icon={CORE_NAV[1].icon}
              label={CORE_NAV[1].label}
              to={CORE_NAV[1].to}
              isActive={isPrefixActive(pathname, CORE_NAV[1].activePrefixes)}
            />
            <BottomNavItem icon={FiGrid} label="Modules" isActive={false} onClick={onOpenModules} />
          </>
        ) : (
          <>
            <BottomNavItem icon={FiHome} label="Hub" to="/dashboard" isActive={pathname === '/dashboard'} />
            {visibleItems.map((item) => (
              <BottomNavItem
                key={item.to}
                icon={item.icon}
                label={item.label}
                to={item.to}
                isActive={isPrefixActive(pathname, item.activePrefixes)}
              />
            ))}
            {overflowItems.length > 0 && (
              <BottomNavItem
                icon={FiMoreHorizontal}
                label="More"
                isActive={overflowItems.some((i) => isPrefixActive(pathname, i.activePrefixes))}
                onClick={moreSheet.onOpen}
              />
            )}
          </>
        )}
      </HStack>

      <Drawer.Root placement='bottom' open={moreSheet.open} onOpenChange={e => {
        if (!e.open) {
          moreSheet.onClose();
        }
      }}>
        <Portal>

          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content borderTopRadius="lg">
              <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
              <Drawer.Header>More</Drawer.Header>
              <Drawer.Body pb="env(safe-area-inset-bottom, 16px)">
                <VStack align="stretch" gap={2}>
                  {overflowItems.map((item) => (
                    <Button
                      key={item.to}
                      variant="ghost"
                      justifyContent="flex-start"
                      onClick={() => {
                        navigate(item.to)
                        moreSheet.onClose()
                      }}><Icon asChild><item.icon /></Icon>{item.label}</Button>
                  ))}
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>

        </Portal>
      </Drawer.Root>
    </>
  );
}
